/**
 * GeneratorsTablePage
 * Displays all generators in a UDC table
 * Route: /apps/power_generators/table
 */

import React from 'react';
import { Tag } from 'antd';
import { UniversalTable } from '@/components/features/udc/UniversalTable';

// Status colors matching the map markers
const statusColors = {
  available: '#52c41a', // Green
  rented: '#1890ff', // Blue
  maintenance: '#faad14', // Orange
  broken: '#ff4d4f', // Red
  decommissioned: '#8c8c8c', // Gray
};

const statusLabels = {
  available: 'Dostupan',
  rented: 'Iznajmljen',
  maintenance: 'Na održavanju',
  broken: 'Pokvaren',
  decommissioned: 'Van upotrebe',
};

export const GeneratorsTablePage: React.FC = () => {
  return (
    <UniversalTable
      collection="generators"
      title="Generatori"
      searchPlaceholder="Pretraži po serijskom broju, proizvođaču, modelu..."
      customRenderers={{
        // Custom renderer for status column with colored tags
        status: (value: string) => {
          const color =
            statusColors[value as keyof typeof statusColors] || 'default';
          const label =
            statusLabels[value as keyof typeof statusLabels] || value;
          return <Tag color={color}>{label}</Tag>;
        },

        // Custom renderer for operational status
        operational_status: (value: string) => {
          const operationalLabels: Record<string, string> = {
            running: 'U radu',
            standby: 'U pripravnosti',
            off: 'Isključen',
            error: 'Greška',
          };
          const operationalColors: Record<string, string> = {
            running: 'green',
            standby: 'blue',
            off: 'default',
            error: 'red',
          };
          return (
            <Tag color={operationalColors[value] || 'default'}>
              {operationalLabels[value] || value}
            </Tag>
          );
        },

        // Custom renderer for power (show kVA with unit)
        power_kva: (value: number | string) => {
          if (value === null || value === undefined) return '-';
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return `${numValue.toFixed(1)} kVA`;
        },

        power_kw: (value: number | string) => {
          if (value === null || value === undefined) return '-';
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return `${numValue.toFixed(1)} kW`;
        },

        // Custom renderer for operating hours
        total_operating_hours: (value: number | string) => {
          if (value === null || value === undefined) return '-';
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return `${numValue.toFixed(1)} h`;
        },

        hours_since_service: (value: number | string) => {
          if (value === null || value === undefined) return '-';
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          return `${numValue.toFixed(1)} h`;
        },

        // Custom renderer for fuel type
        fuel_type: (value: string) => {
          const fuelLabels: Record<string, string> = {
            diesel: 'Dizel',
            petrol: 'Benzin',
            gas: 'Gas',
            hybrid: 'Hibrid',
          };
          return fuelLabels[value] || value;
        },

        // Custom renderer for location type
        location_type: (value: string) => {
          const locationLabels: Record<string, string> = {
            warehouse: 'Skladište',
            client_site: 'Kod klijenta',
            service: 'Servis',
            in_transit: 'U transportu',
          };
          return locationLabels[value] || value;
        },

        // Custom renderer for active status (boolean)
        is_active: (value: boolean) => {
          return value ? (
            <Tag color="green">Aktivan</Tag>
          ) : (
            <Tag color="default">Neaktivan</Tag>
          );
        },
      }}
    />
  );
};
